import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { DevToolsService } from '../dev-tools.service';

type JsonKind = 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null' | 'undefined' | 'date' | 'circular';

interface JsonNodeView {
  kind: JsonKind;
  preview: string;
  entries: Array<{ key: string; value: unknown }>;
  length: number;
}

interface Segment {
  text: string;
  match: boolean;
}

/**
 * JSON tree collapsible, récursif, OnPush.
 * Gère primitives, arrays, objects, Date, cycles.
 * Pagination pour les arrays > 50.
 * Highlight : clés + substrings primitives + auto-expand des ancêtres d'un match.
 */
@Component({
  selector: 'dev-json-tree',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
    @let k = kind();
    @let segs = keySegments();
    @if (k === 'object' || k === 'array') {
      <div class="tw-font-mono tw-text-[11px] tw-leading-[1.35]">
        <div
          class="tw-flex tw-items-start tw-gap-1 tw-group tw-rounded"
          [ngClass]="keyMatches() ? 'tw-bg-alert-500/10' : ''"
        >
          <button
            type="button"
            class="tw-w-3 tw-h-3 tw-mt-[2px] tw-flex tw-items-center tw-justify-center tw-text-gray-400 hover:tw-text-success-500 tw-shrink-0"
            (click)="toggle()"
            [attr.aria-label]="expanded() ? 'Replier' : 'Déplier'"
          >
            <i class="ph" [ngClass]="expanded() ? 'ph-caret-down' : 'ph-caret-right'"></i>
          </button>
          @if (keyName() !== undefined) {
            <span class="tw-text-gray-300">
              "@for (s of segs; track $index) {
                @if (s.match) {
                  <span class="tw-bg-alert-500/40 tw-text-alert-500 tw-rounded-sm tw-px-[1px]">{{ s.text }}</span>
                } @else {
                  <span>{{ s.text }}</span>
                }
              }"
            </span>
            <span class="tw-text-gray-500">:</span>
          }
          <span class="tw-text-gray-400">{{ view().preview }}</span>
          @if (deepMatchCount() > 0 && !keyMatches()) {
            <span class="tw-text-alert-500 tw-text-[10px] tw-ml-1" [title]="deepMatchCount() + ' correspondance(s) à l\\'intérieur'">
              <i class="ph ph-magnifying-glass-plus"></i> {{ deepMatchCount() }}
            </span>
          }
          <button
            type="button"
            class="tw-ml-1 tw-opacity-0 group-hover:tw-opacity-100 tw-text-gray-500 hover:tw-text-success-500 tw-transition"
            title="Copier la valeur JSON"
            (click)="copy($event)"
          >
            <i class="ph ph-copy tw-text-[10px]"></i>
          </button>
        </div>
        @if (expanded()) {
          <div class="tw-pl-4 tw-border-l tw-border-gray-700/60 tw-ml-[6px]">
            @for (entry of visibleEntries(); track entry.key) {
              <dev-json-tree
                [value]="entry.value"
                [keyName]="entry.key"
                [depth]="depth() + 1"
                [maxExpandDepth]="maxExpandDepth()"
                [search]="search()"
              />
            }
            @if (view().length > visibleCount()) {
              <button
                type="button"
                class="tw-text-[10px] tw-text-success-500 hover:tw-text-success-500/80 tw-px-2 tw-py-0.5 tw-mt-1"
                (click)="showMore()"
              >
                Afficher plus ({{ view().length - visibleCount() }} restants)
              </button>
            }
          </div>
        }
      </div>
    } @else {
      <div
        class="tw-font-mono tw-text-[11px] tw-leading-[1.35] tw-flex tw-items-center tw-gap-1 tw-group tw-rounded"
        [ngClass]="rowMatches() ? 'tw-bg-alert-500/10' : ''"
      >
        <span class="tw-w-3 tw-inline-block"></span>
        @if (keyName() !== undefined) {
          <span class="tw-text-gray-300">
            "@for (s of segs; track $index) {
              @if (s.match) {
                <span class="tw-bg-alert-500/40 tw-text-alert-500 tw-rounded-sm tw-px-[1px]">{{ s.text }}</span>
              } @else {
                <span>{{ s.text }}</span>
              }
            }"
          </span>
          <span class="tw-text-gray-500">:</span>
        }
        <span [ngClass]="primitiveClass()">
          @for (s of primitiveSegments(); track $index) {
            @if (s.match) {
              <span class="tw-bg-alert-500/40 tw-rounded-sm tw-px-[1px]">{{ s.text }}</span>
            } @else {
              <span>{{ s.text }}</span>
            }
          }
        </span>
        <button
          type="button"
          class="tw-ml-1 tw-opacity-0 group-hover:tw-opacity-100 tw-text-gray-500 hover:tw-text-success-500 tw-transition"
          title="Copier la valeur"
          (click)="copy($event)"
        >
          <i class="ph ph-copy tw-text-[10px]"></i>
        </button>
      </div>
    }
  `,
})
export class JsonTreeComponent {
  private readonly devTools = inject(DevToolsService);

  readonly value = input<unknown>(undefined);
  readonly keyName = input<string | undefined>(undefined);
  readonly depth = input<number>(0);
  readonly defaultExpanded = input<boolean | undefined>(undefined);
  readonly maxExpandDepth = input<number | undefined>(undefined);
  readonly search = input<string>('');

  private readonly _userToggled = signal<boolean | null>(null);
  private readonly _pageSize = 50;
  readonly visibleCount = signal<number>(this._pageSize);

  readonly kind = computed<JsonKind>(() => this.detectKind(this.value()));

  readonly view = computed<JsonNodeView>(() => {
    const v = this.value();
    const k = this.kind();
    if (k === 'array') {
      const arr = v as unknown[];
      return {
        kind: k,
        preview: `Array(${arr.length})`,
        entries: arr.map((val, i) => ({ key: String(i), value: val })),
        length: arr.length,
      };
    }
    if (k === 'object') {
      const obj = v as Record<string, unknown>;
      const entries = Object.keys(obj).map((key) => ({ key, value: obj[key] }));
      return {
        kind: k,
        preview: `{${entries.length}}`,
        entries,
        length: entries.length,
      };
    }
    return { kind: k, preview: '', entries: [], length: 0 };
  });

  readonly visibleEntries = computed(() => this.view().entries.slice(0, this.visibleCount()));

  /** Le nom de la clé contient la recherche ? */
  readonly keyMatches = computed<boolean>(() => {
    const q = this.normalizedQuery();
    if (!q) return false;
    const key = this.keyName();
    return !!key && key.toLowerCase().includes(q);
  });

  /** La valeur primitive contient la recherche ? */
  readonly valueMatches = computed<boolean>(() => {
    const q = this.normalizedQuery();
    if (!q) return false;
    const k = this.kind();
    if (k === 'object' || k === 'array') return false;
    return this.primitiveDisplay().toLowerCase().includes(q);
  });

  readonly rowMatches = computed<boolean>(() => this.keyMatches() || this.valueMatches());

  /** Compte les matches dans les descendants (profondeur complète). */
  readonly deepMatchCount = computed<number>(() => {
    const q = this.normalizedQuery();
    if (!q) return 0;
    const v = this.value();
    const k = this.kind();
    if (k !== 'object' && k !== 'array') return 0;
    return this.countMatchesDeep(v, q, new WeakSet());
  });

  /** Auto-expand si un match existe dans le sous-arbre ou selon maxExpandDepth. */
  readonly expanded = computed<boolean>(() => {
    const user = this._userToggled();
    if (user !== null) return user;
    const q = this.normalizedQuery();
    if (q && this.deepMatchCount() > 0) return true;
    const targetDepth = this.maxExpandDepth();
    if (targetDepth !== undefined) {
      return this.depth() < targetDepth;
    }
    const def = this.defaultExpanded();
    if (def !== undefined) return def;
    return this.depth() < 2;
  });

  readonly primitiveDisplay = computed<string>(() => {
    const v = this.value();
    switch (this.kind()) {
      case 'string': return `"${v}"`;
      case 'number': return String(v);
      case 'boolean': return String(v);
      case 'null': return 'null';
      case 'undefined': return 'undefined';
      case 'date': return `Date("${(v as Date).toISOString()}")`;
      case 'circular': return '[Circular]';
      default: return String(v);
    }
  });

  readonly primitiveClass = computed<string>(() => {
    switch (this.kind()) {
      case 'string': return 'tw-text-success-500';
      case 'number': return 'tw-text-warning-500';
      case 'boolean': return 'tw-text-gov-secondary';
      case 'null':
      case 'undefined': return 'tw-text-gray-500 tw-italic';
      case 'date': return 'tw-text-info-500';
      case 'circular': return 'tw-text-error-500 tw-italic';
      default: return 'tw-text-gray-300';
    }
  });

  /** Segments de la clé pour rendu avec surlignage du substring matché. */
  readonly keySegments = computed<Segment[]>(() => {
    const key = this.keyName();
    if (key === undefined) return [];
    return this.splitSegments(key, this.normalizedQuery());
  });

  /** Segments de la valeur primitive pour rendu avec surlignage. */
  readonly primitiveSegments = computed<Segment[]>(() => {
    const k = this.kind();
    if (k === 'object' || k === 'array') return [];
    return this.splitSegments(this.primitiveDisplay(), this.normalizedQuery());
  });

  private readonly normalizedQuery = computed<string>(() => this.search().trim().toLowerCase());

  // ── Helpers ──────────────────────────────────────────────────────────

  toggle(): void {
    this._userToggled.set(!this.expanded());
  }

  showMore(): void {
    this.visibleCount.update((n) => n + this._pageSize);
  }

  copy(ev: Event): void {
    ev.stopPropagation();
    const text = this.devTools.serialize(this.value());
    try {
      navigator.clipboard?.writeText(text);
    } catch {
      /* ignore */
    }
  }

  /** Découpe `text` en segments matchés / non-matchés (case-insensitive). */
  private splitSegments(text: string, q: string): Segment[] {
    if (!q) return [{ text, match: false }];
    const lower = text.toLowerCase();
    const out: Segment[] = [];
    let i = 0;
    while (i < text.length) {
      const idx = lower.indexOf(q, i);
      if (idx === -1) {
        out.push({ text: text.slice(i), match: false });
        break;
      }
      if (idx > i) out.push({ text: text.slice(i, idx), match: false });
      out.push({ text: text.slice(idx, idx + q.length), match: true });
      i = idx + q.length;
    }
    return out;
  }

  /** Compte récursivement les matches (clés + valeurs primitives) sous une valeur. */
  private countMatchesDeep(v: unknown, q: string, seen: WeakSet<object>): number {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'object') {
      if (seen.has(v as object)) return 0;
      seen.add(v as object);
      let count = 0;
      if (Array.isArray(v)) {
        for (let i = 0; i < v.length; i++) {
          if (String(i).toLowerCase().includes(q)) count++;
          count += this.countMatchesDeep(v[i], q, seen);
        }
      } else if (v instanceof Date) {
        if (v.toISOString().toLowerCase().includes(q)) count++;
      } else {
        for (const key of Object.keys(v as Record<string, unknown>)) {
          if (key.toLowerCase().includes(q)) count++;
          count += this.countMatchesDeep((v as Record<string, unknown>)[key], q, seen);
        }
      }
      return count;
    }
    const s = typeof v === 'string' ? `"${v}"` : String(v);
    return s.toLowerCase().includes(q) ? 1 : 0;
  }

  private detectKind(v: unknown): JsonKind {
    if (v === null) return 'null';
    if (v === undefined) return 'undefined';
    if (v instanceof Date) return 'date';
    if (Array.isArray(v)) return 'array';
    const t = typeof v;
    if (t === 'string') return 'string';
    if (t === 'number') return 'number';
    if (t === 'boolean') return 'boolean';
    if (t === 'object') return 'object';
    return 'string';
  }
}
